import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";

const QUESTIONS = [
  {
    key: "focus",
    q: "Where does your attention feel most drawn right now?",
    options: [
      "Healing old wounds & self-love",
      "Growing my business with meaning",
      "Manifesting a specific dream",
      "Communication & interpersonal skills",
      "My teenager's wellbeing",
      "Reconnecting with nature & the land",
    ],
  },
  {
    key: "stage",
    q: "How would you describe your current stage?",
    options: [
      "I feel stuck and need release",
      "I'm building and need alignment",
      "I'm ready but need clarity",
      "I want to guide someone I love",
      "I'm called back to slower living",
    ],
  },
  {
    key: "time",
    q: "How much time can you dedicate weekly?",
    options: [
      "A weekend intensive",
      "A few hours a week for weeks",
      "Ongoing 1:1 sessions",
      "Just curious, exploring",
    ],
  },
  {
    key: "vibe",
    q: "What draws you most?",
    options: [
      "Deep inner work",
      "Practical tools & frameworks",
      "Community and shared journey",
      "Solitude with structure",
    ],
  },
];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const choose = (val) => {
    const nextAnswers = { ...answers, [QUESTIONS[step].key]: val };
    setAnswers(nextAnswers);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      submit(nextAnswers);
    }
  };

  const submit = async (finalAnswers) => {
    setLoading(true);
    try {
      const { data } = await api.post("/quiz/recommend", { answers: finalAnswers });
      setResult(data);
    } catch {
      setResult({ program_id: "hyl-workshop", title: "Heal Your Life® Workshop", reason: "A beautiful place to begin.", program: null });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setResult(null);
  };

  return (
    <div data-testid="quiz-page" className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-ub bg-ub-alt text-sm">
        <Sparkles size={14} className="text-primary-ub" />
        <span className="text-ub-2">AI-guided · takes ~1 minute</span>
      </div>
      <h1 className="font-serif text-5xl md:text-6xl mt-6 leading-[1.02] tracking-tighter">
        Find your <span className="italic text-primary-ub">next</span> doorway.
      </h1>
      <p className="mt-4 text-lg text-ub-2 max-w-xl">Four gentle questions. One thoughtful recommendation from Anant's philosophy.</p>

      {!result && !loading && (
        <div className="mt-12" data-testid="quiz-question-box">
          <div className="h-1.5 bg-ub-alt rounded-full overflow-hidden">
            <div className="h-full bg-primary-ub transition-all duration-500" style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
          </div>
          <div className="text-sm uppercase tracking-widest text-muted-ub mt-4">Question {step + 1} of {QUESTIONS.length}</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-snug">{QUESTIONS[step].q}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8">
            {QUESTIONS[step].options.map((opt) => (
              <button
                key={opt}
                onClick={() => choose(opt)}
                data-testid={`quiz-option-${opt.slice(0, 15).replace(/\s/g, "-")}`}
                className="text-left px-5 py-4 rounded-2xl bg-ub border border-ub hover:border-[var(--ub-primary)] hover:bg-primary-soft transition-all group"
              >
                <span className="text-ub group-hover:text-primary-ub">{opt}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-16 text-center" data-testid="quiz-loading">
          <div className="font-script text-4xl text-primary-ub">Listening…</div>
          <p className="text-ub-2 mt-2">Anant's advisor is choosing the doorway for you.</p>
        </div>
      )}

      {result && (
        <div className="mt-12 bg-ub-alt border border-ub rounded-[2rem] p-8 md:p-12" data-testid="quiz-result">
          <div className="text-sm uppercase tracking-widest text-primary-ub">Recommended for you</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">{result.title}</h2>
          <p className="mt-5 text-lg text-ub-2 leading-relaxed italic">"{result.reason}"</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={`/programs/${result.program_id}`} data-testid="quiz-cta-view" className="inline-flex items-center gap-2 bg-primary-ub text-white rounded-full px-7 py-3.5 hover:bg-[var(--ub-primary-hover)] transition-colors">
              View this program <ArrowRight size={16} />
            </Link>
            <button onClick={reset} data-testid="quiz-retake" className="rounded-full px-7 py-3.5 border border-ub-dark hover:bg-ub-dark hover:text-white transition-colors">
              Retake quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
